import React, { useState } from 'react';
import { DollarSignIcon, LayersIcon, UserIcon, UsersIcon, BuildingIcon, HomeIcon, TagIcon, GitBranchIcon, ArrowUpRightIcon, AlertTriangleIcon, ClockIcon, EyeIcon, CheckCircleIcon, LockIcon, CalendarIcon, PercentIcon, HelpCircleIcon, PlusIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon, SaveIcon } from 'lucide-react';
import { StatusBadge } from '../../StatusBadge';
type FDOAMode = 'Override' | 'Satisfy' | 'CostCentreOnly';
type FDOARuleProps = {
  rule: any;
  onSave: (rule: any) => void;
  onCancel: () => void;
  locale: string;
};
export function FDOARuleEditor({
  rule,
  onSave,
  onCancel,
  locale
}: FDOARuleProps) {
  // Initialize state with rule data or defaults
  const [ruleData, setRuleData] = useState({
    ...rule,
    modes: rule.modes || {
      vendorMode: 'Satisfy' as FDOAMode,
      siteMode: 'Satisfy' as FDOAMode,
      entityMode: 'Override' as FDOAMode
    },
    delegation: rule.delegation || {
      inheritUpperLimit: true,
      useOriginalChain: true,
      maxHops: 5
    },
    fourEyes: rule.fourEyes || {
      enabled: true,
      threshold: 10000,
      applyAt: 'Both',
      currency: 'AUD'
    },
    emergency: rule.emergency || {
      enabled: false,
      approvers: []
    },
    lineLevel: rule.lineLevel || {
      enabled: true,
      evaluationMode: 'Sequential',
      categoriesOnly: true
    },
    preApproval: rule.preApproval || {
      checkClosedPeriods: true
    },
    multiCurrency: rule.multiCurrency || {
      enabled: true,
      baseCurrency: 'AUD',
      evaluateAt: 'Header'
    },
    audit: rule.audit || {
      enabled: true,
      storeDecisions: true
    }
  });
  // Sample data for dropdowns
  const [entities] = useState([{
    id: 'entity-001',
    name: 'Global Technologies Inc.'
  }, {
    id: 'entity-002',
    name: 'European Operations Ltd'
  }, {
    id: 'entity-003',
    name: 'APAC Subsidiary'
  }]);
  const [users] = useState([{
    id: 'user-001',
    name: 'John Smith'
  }, {
    id: 'user-002',
    name: 'Jane Doe'
  }, {
    id: 'user-003',
    name: 'Michael Johnson'
  }, {
    id: 'user-004',
    name: 'Sarah Williams'
  }]);
  const [roles] = useState([{
    id: 'role-001',
    name: 'Finance Manager'
  }, {
    id: 'role-002',
    name: 'Department Head'
  }, {
    id: 'role-003',
    name: 'CEO'
  }, {
    id: 'role-004',
    name: 'CFO'
  }]);
  const [glCategories] = useState([{
    id: 'cat-001',
    code: '1000',
    description: 'IT Equipment',
    entityId: 'entity-001'
  }, {
    id: 'cat-002',
    code: '2000',
    description: 'Office Supplies',
    entityId: 'entity-001'
  }, {
    id: 'cat-003',
    code: '3000',
    description: 'Professional Services',
    entityId: 'entity-001'
  }, {
    id: 'cat-004',
    code: '4000',
    description: 'Travel Expenses',
    entityId: 'entity-001'
  }]);
  const [currencies] = useState([{
    code: 'AUD',
    name: 'Australian Dollar'
  }, {
    code: 'USD',
    name: 'US Dollar'
  }, {
    code: 'EUR',
    name: 'Euro'
  }, {
    code: 'GBP',
    name: 'British Pound'
  }]);
  // UI state
  const [expandedSections, setExpandedSections] = useState({
    defaults: true,
    modes: true,
    delegation: true,
    categories: true,
    emergency: false,
    fourEyes: true,
    lineLevel: false,
    periods: false,
    multiCurrency: false,
    audit: false
  });
  const [defaultApprovers, setDefaultApprovers] = useState(rule.defaultApprovers || [{
    id: 'default-001',
    scopeType: 'Entity',
    scopeId: 'entity-001',
    userOrRoleId: 'role-001',
    isReviewer: false,
    isApprover: true,
    fdoaLimit: 10000,
    currency: 'AUD',
    belongsToCategories: ['cat-001', 'cat-002']
  }, {
    id: 'default-002',
    scopeType: 'Vendor',
    scopeId: 'vendor-001',
    userOrRoleId: 'user-002',
    isReviewer: true,
    isApprover: false,
    fdoaLimit: 0,
    currency: 'AUD',
    belongsToCategories: []
  }]);
  const [categoryApprovers, setCategoryApprovers] = useState(rule.categoryApprovers || [{
    id: 'catapp-001',
    categoryId: 'cat-001',
    sequence: 1,
    userOrRoleId: 'user-001',
    limit: 5000,
    currency: 'AUD'
  }, {
    id: 'catapp-002',
    categoryId: 'cat-001',
    sequence: 2,
    userOrRoleId: 'role-001',
    limit: 25000,
    currency: 'AUD'
  }, {
    id: 'catapp-003',
    categoryId: 'cat-001',
    sequence: 3,
    userOrRoleId: 'role-003',
    limit: 100000,
    currency: 'AUD'
  }]);
  const [accountingPeriods, setAccountingPeriods] = useState(rule.accountingPeriods || [{
    id: 'period-001',
    entityId: 'entity-001',
    periodCode: 'FY23-Q3',
    startUtc: '2023-07-01T00:00:00Z',
    endUtc: '2023-09-30T23:59:59Z',
    isClosed: true
  }, {
    id: 'period-002',
    entityId: 'entity-001',
    periodCode: 'FY23-Q4',
    startUtc: '2023-10-01T00:00:00Z',
    endUtc: '2023-12-31T23:59:59Z',
    isClosed: false
  }]);
  // Helper functions
  const toggleSection = (section: string) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  const handleModeChange = (scope: 'vendorMode' | 'siteMode' | 'entityMode', mode: FDOAMode) => {
    setRuleData({
      ...ruleData,
      modes: {
        ...ruleData.modes,
        [scope]: mode
      }
    });
  };
  const handleDelegationChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      delegation: {
        ...ruleData.delegation,
        [field]: value
      }
    });
  };
  const handleFourEyesChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      fourEyes: {
        ...ruleData.fourEyes,
        [field]: value
      }
    });
  };
  const handleEmergencyChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      emergency: {
        ...ruleData.emergency,
        [field]: value
      }
    });
  };
  const handleLineLevelChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      lineLevel: {
        ...ruleData.lineLevel,
        [field]: value
      }
    });
  };
  const handleMultiCurrencyChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      multiCurrency: {
        ...ruleData.multiCurrency,
        [field]: value
      }
    });
  };
  const addDefaultApprover = () => {
    const newApprover = {
      id: `default-${Date.now()}`,
      scopeType: 'Entity',
      scopeId: '',
      userOrRoleId: '',
      isReviewer: false,
      isApprover: true,
      fdoaLimit: 0,
      currency: 'AUD',
      belongsToCategories: []
    };
    setDefaultApprovers([...defaultApprovers, newApprover]);
  };
  const updateDefaultApprover = (index: number, field: string, value: any) => {
    const updatedApprovers = [...defaultApprovers];
    updatedApprovers[index] = {
      ...updatedApprovers[index],
      [field]: value
    };
    setDefaultApprovers(updatedApprovers);
  };
  const removeDefaultApprover = (index: number) => {
    const updatedApprovers = [...defaultApprovers];
    updatedApprovers.splice(index, 1);
    setDefaultApprovers(updatedApprovers);
  };
  const addCategoryApprover = () => {
    const newApprover = {
      id: `catapp-${Date.now()}`,
      categoryId: '',
      sequence: categoryApprovers.length + 1,
      userOrRoleId: '',
      limit: 0,
      currency: 'AUD'
    };
    setCategoryApprovers([...categoryApprovers, newApprover]);
  };
  const updateCategoryApprover = (index: number, field: string, value: any) => {
    const updatedApprovers = [...categoryApprovers];
    updatedApprovers[index] = {
      ...updatedApprovers[index],
      [field]: value
    };
    setCategoryApprovers(updatedApprovers);
  };
  const removeCategoryApprover = (index: number) => {
    const updatedApprovers = [...categoryApprovers];
    updatedApprovers.splice(index, 1);
    setCategoryApprovers(updatedApprovers);
  };
  const addEmergencyApprover = (userOrRoleId: string) => {
    if (!ruleData.emergency.approvers.includes(userOrRoleId)) {
      setRuleData({
        ...ruleData,
        emergency: {
          ...ruleData.emergency,
          approvers: [...ruleData.emergency.approvers, userOrRoleId]
        }
      });
    }
  };
  const removeEmergencyApprover = (userOrRoleId: string) => {
    setRuleData({
      ...ruleData,
      emergency: {
        ...ruleData.emergency,
        approvers: ruleData.emergency.approvers.filter(id => id !== userOrRoleId)
      }
    });
  };
  const addAccountingPeriod = () => {
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const newPeriod = {
      id: `period-${Date.now()}`,
      entityId: 'entity-001',
      periodCode: `FY${today.getFullYear().toString().substr(2)}-Q${Math.floor(today.getMonth() / 3) + 1}`,
      startUtc: today.toISOString(),
      endUtc: nextMonth.toISOString(),
      isClosed: false
    };
    setAccountingPeriods([...accountingPeriods, newPeriod]);
  };
  const updateAccountingPeriod = (index: number, field: string, value: any) => {
    const updatedPeriods = [...accountingPeriods];
    updatedPeriods[index] = {
      ...updatedPeriods[index],
      [field]: value
    };
    setAccountingPeriods(updatedPeriods);
  };
  const removeAccountingPeriod = (index: number) => {
    const updatedPeriods = [...accountingPeriods];
    updatedPeriods.splice(index, 1);
    setAccountingPeriods(updatedPeriods);
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const updatedRule = {
      ...rule,
      ...ruleData,
      defaultApprovers,
      categoryApprovers,
      accountingPeriods
    };
    onSave(updatedRule);
  };
  // Render the form
  return <form onSubmit={handleSubmit} className="space-y-6" data-id="element-8776">
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-8777">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center" data-id="element-8778">
          <div data-id="element-8779">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-8780">
              Financial Delegation Of Authority (FDOA) Rule Configuration
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-8781">
              Configure approval pathways based on financial delegation limits
              and cost centers
            </p>
          </div>
          <div className="flex space-x-3" data-id="element-8782">
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8783">
              Cancel
            </button>
            <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8784">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-8785" />
              Save Rule
            </button>
          </div>
        </div>
        {/* Basic Information */}
        <div className="px-4 py-5 sm:p-6 border-b border-gray-200" data-id="element-8786">
          <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-8787">
            Basic Information
          </h4>
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-8788">
            <div className="sm:col-span-3" data-id="element-8789">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-8790">
                Rule Name
              </label>
              <div className="mt-1" data-id="element-8791">
                <input type="text" id="name" value={ruleData.name} onChange={e => setRuleData({
                ...ruleData,
                name: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8792" />
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-8793">
              <label htmlFor="version" className="block text-sm font-medium text-gray-700" data-id="element-8794">
                Version
              </label>
              <div className="mt-1" data-id="element-8795">
                <input type="text" id="version" value={ruleData.version} onChange={e => setRuleData({
                ...ruleData,
                version: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8796" />
              </div>
            </div>
            <div className="sm:col-span-6" data-id="element-8797">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-8798">
                Description
              </label>
              <div className="mt-1" data-id="element-8799">
                <textarea id="description" rows={2} value={ruleData.description} onChange={e => setRuleData({
                ...ruleData,
                description: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8800" />
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-8801">
              <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-8802">
                Status
              </label>
              <div className="mt-1" data-id="element-8803">
                <select id="status" value={ruleData.status} onChange={e => setRuleData({
                ...ruleData,
                status: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8804">
                  <option value="Draft" data-id="element-8805">Draft</option>
                  <option value="Active" data-id="element-8806">Active</option>
                  <option value="Inactive" data-id="element-8807">Inactive</option>
                </select>
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-8808">
              <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-8809">
                Scope
              </label>
              <div className="mt-1" data-id="element-8810">
                <select id="scope" value={ruleData.scope} onChange={e => setRuleData({
                ...ruleData,
                scope: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8811">
                  <option value="Entity" data-id="element-8812">Entity</option>
                  <option value="Global" data-id="element-8813">Global</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        {/* Default Approvers Section */}
        <div className="border-b border-gray-200" data-id="element-8814">
          <button type="button" onClick={() => toggleSection('defaults')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-8815">
            <div className="flex items-center" data-id="element-8816">
              <LayersIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-8817" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-8818">
                Default Approvers (Vendor/Site/Entity)
              </h3>
            </div>
            {expandedSections.defaults ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-8819" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-8820" />}
          </button>
          {expandedSections.defaults && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-8821">
              <p className="text-sm text-gray-500 mb-4" data-id="element-8822">
                Configure default approvers for vendors, sites, and entities.
                These can be reviewers, approvers, or both.
              </p>
              <div className="space-y-4" data-id="element-8823">
                {defaultApprovers.map((approver, index) => <div key={approver.id} className="border border-gray-200 rounded-md p-4" data-id="element-8824">
                    <div className="flex justify-between items-start" data-id="element-8825">
                      <div className="flex items-center" data-id="element-8826">
                        {approver.scopeType === 'Entity' ? <BuildingIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-8827" /> : approver.scopeType === 'Site' ? <HomeIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-8828" /> : <TagIcon className="h-5 w-5 text-orange-500 mr-2" data-id="element-8829" />}
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-8830">
                          {approver.scopeType} Default Approver
                        </h4>
                      </div>
                      <button type="button" onClick={() => removeDefaultApprover(index)} className="text-red-600 hover:text-red-900" data-id="element-8831">
                        <TrashIcon className="h-4 w-4" data-id="element-8832" />
                      </button>
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-8833">
                      <div className="sm:col-span-2" data-id="element-8834">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-8835">
                          Scope Type
                        </label>
                        <select value={approver.scopeType} onChange={e => updateDefaultApprover(index, 'scopeType', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-8836">
                          <option value="Entity" data-id="element-8837">Entity</option>
                          <option value="Site" data-id="element-8838">Site</option>
                          <option value="Vendor" data-id="element-8839">Vendor</option>
                        </select>
                      </div>
                      <div className="sm:col-span-4" data-id="element-8840">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-8841">
                          {approver.scopeType} Name
                        </label>
                        <select value={approver.scopeId} onChange={e => updateDefaultApprover(index, 'scopeId', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-8842">
                          <option value="" data-id="element-8843">Select {approver.scopeType}</option>
                          {approver.scopeType === 'Entity' && entities.map(entity => <option key={entity.id} value={entity.id} data-id="element-8844">
                                {entity.name}
                              </option>)}
                          {/* Add similar options for Site and Vendor when available */}
                        </select>
                      </div>
                      <div className="sm:col-span-3" data-id="element-8845">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-8846">
                          User or Role
                        </label>
                        <select value={approver.userOrRoleId} onChange={e => updateDefaultApprover(index, 'userOrRoleId', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-8847">
                          <option value="" data-id="element-8848">Select User or Role</option>
                          <optgroup label="Users" data-id="element-8849">
                            {users.map(user => <option key={user.id} value={user.id} data-id="element-8850">
                                {user.name}
                              </option>)}
                          </optgroup>
                          <optgroup label="Roles" data-id="element-8851">
                            {roles.map(role => <option key={role.id} value={role.id} data-id="element-8852">
                                {role.name}
                              </option>)}
                          </optgroup>
                        </select>
                      </div>
                      <div className="sm:col-span-3" data-id="element-8853">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-8854">
                          Approval Type
                        </label>
                        <div className="mt-2 flex items-center space-x-4" data-id="element-8855">
                          <div className="flex items-center" data-id="element-8856">
                            <input id={`reviewer-${approver.id}`} type="checkbox" checked={approver.isReviewer} onChange={e => updateDefaultApprover(index, 'isReviewer', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-8857" />
                            <label htmlFor={`reviewer-${approver.id}`} className="ml-2 block text-sm text-gray-700" data-id="element-8858">
                              Reviewer
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-8859">
                            <input id={`approver-${approver.id}`} type="checkbox" checked={approver.isApprover} onChange={e => updateDefaultApprover(index, 'isApprover', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-8860" />
                            <label htmlFor={`approver-${approver.id}`} className="ml-2 block text-sm text-gray-700" data-id="element-8861">
                              Approver
                            </label>
                          </div>
                        </div>
                      </div>
                      {approver.isApprover && <>
                          <div className="sm:col-span-2" data-id="element-8862">
                            <label className="block text-xs font-medium text-gray-700" data-id="element-8863">
                              FDOA Limit
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm" data-id="element-8864">
                              <input type="number" value={approver.fdoaLimit} onChange={e => updateDefaultApprover(index, 'fdoaLimit', parseFloat(e.target.value))} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-3 pr-12 sm:text-sm border-gray-300 rounded-md" data-id="element-8865" />
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-8866">
                                <span className="text-gray-500 sm:text-sm" data-id="element-8867">
                                  {approver.currency}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="sm:col-span-2" data-id="element-8868">
                            <label className="block text-xs font-medium text-gray-700" data-id="element-8869">
                              Currency
                            </label>
                            <select value={approver.currency} onChange={e => updateDefaultApprover(index, 'currency', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-8870">
                              {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-8871">
                                  {currency.code}
                                </option>)}
                            </select>
                          </div>
                        </>}
                      <div className="sm:col-span-6" data-id="element-8872">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-8873">
                          Belongs to Categories (Optional)
                        </label>
                        <select multiple value={approver.belongsToCategories} onChange={e => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    updateDefaultApprover(index, 'belongsToCategories', selected);
                  }} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" size={3} data-id="element-8874">
                          {glCategories.map(category => <option key={category.id} value={category.id} data-id="element-8875">
                              {category.code} - {category.description}
                            </option>)}
                        </select>
                        <p className="mt-1 text-xs text-gray-500" data-id="element-8876">
                          Hold Ctrl/Cmd to select multiple categories
                        </p>
                      </div>
                    </div>
                  </div>)}
                <button type="button" onClick={addDefaultApprover} className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8877">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-8878" />
                  Add Default Approver
                </button>
              </div>
            </div>}
        </div>
        {/* Modes Section */}
        <div className="border-b border-gray-200" data-id="element-8879">
          <button type="button" onClick={() => toggleSection('modes')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-8880">
            <div className="flex items-center" data-id="element-8881">
              <GitBranchIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-8882" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-8883">
                Approval Modes
              </h3>
            </div>
            {expandedSections.modes ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-8884" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-8885" />}
          </button>
          {expandedSections.modes && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-8886">
              <p className="text-sm text-gray-500 mb-4" data-id="element-8887">
                Configure how default approvers interact with cost center
                approvers
              </p>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-3" data-id="element-8888">
                <div data-id="element-8889">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8890">
                    Vendor Mode
                  </label>
                  <div className="space-y-2" data-id="element-8891">
                    <div className="flex items-center" data-id="element-8892">
                      <input id="vendor-override" type="radio" checked={ruleData.modes.vendorMode === 'Override'} onChange={() => handleModeChange('vendorMode', 'Override')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-8893" />
                      <label htmlFor="vendor-override" className="ml-3 block text-sm text-gray-700" data-id="element-8894">
                        Override
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-8895">
                      <input id="vendor-satisfy" type="radio" checked={ruleData.modes.vendorMode === 'Satisfy'} onChange={() => handleModeChange('vendorMode', 'Satisfy')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-8896" />
                      <label htmlFor="vendor-satisfy" className="ml-3 block text-sm text-gray-700" data-id="element-8897">
                        Satisfy
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-8898">
                      <input id="vendor-costcentre" type="radio" checked={ruleData.modes.vendorMode === 'CostCentreOnly'} onChange={() => handleModeChange('vendorMode', 'CostCentreOnly')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-8899" />
                      <label htmlFor="vendor-costcentre" className="ml-3 block text-sm text-gray-700" data-id="element-8900">
                        Cost Centre Only
                      </label>
                    </div>
                  </div>
                </div>
                <div data-id="element-8901">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8902">
                    Site Mode
                  </label>
                  <div className="space-y-2" data-id="element-8903">
                    <div className="flex items-center" data-id="element-8904">
                      <input id="site-override" type="radio" checked={ruleData.modes.siteMode === 'Override'} onChange={() => handleModeChange('siteMode', 'Override')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-8905" />
                      <label htmlFor="site-override" className="ml-3 block text-sm text-gray-700" data-id="element-8906">
                        Override
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-8907">
                      <input id="site-satisfy" type="radio" checked={ruleData.modes.siteMode === 'Satisfy'} onChange={() => handleModeChange('siteMode', 'Satisfy')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-8908" />
                      <label htmlFor="site-satisfy" className="ml-3 block text-sm text-gray-700" data-id="element-8909">
                        Satisfy
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-8910">
                      <input id="site-costcentre" type="radio" checked={ruleData.modes.siteMode === 'CostCentreOnly'} onChange={() => handleModeChange('siteMode', 'CostCentreOnly')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-8911" />
                      <label htmlFor="site-costcentre" className="ml-3 block text-sm text-gray-700" data-id="element-8912">
                        Cost Centre Only
                      </label>
                    </div>
                  </div>
                </div>
                <div data-id="element-8913">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8914">
                    Entity Mode
                  </label>
                  <div className="space-y-2" data-id="element-8915">
                    <div className="flex items-center" data-id="element-8916">
                      <input id="entity-override" type="radio" checked={ruleData.modes.entityMode === 'Override'} onChange={() => handleModeChange('entityMode', 'Override')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-8917" />
                      <label htmlFor="entity-override" className="ml-3 block text-sm text-gray-700" data-id="element-8918">
                        Override
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-8919">
                      <input id="entity-satisfy" type="radio" checked={ruleData.modes.entityMode === 'Satisfy'} onChange={() => handleModeChange('entityMode', 'Satisfy')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-8920" />
                      <label htmlFor="entity-satisfy" className="ml-3 block text-sm text-gray-700" data-id="element-8921">
                        Satisfy
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-8922">
                      <input id="entity-costcentre" type="radio" checked={ruleData.modes.entityMode === 'CostCentreOnly'} onChange={() => handleModeChange('entityMode', 'CostCentreOnly')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-8923" />
                      <label htmlFor="entity-costcentre" className="ml-3 block text-sm text-gray-700" data-id="element-8924">
                        Cost Centre Only
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 bg-gray-50 p-4 rounded-md" data-id="element-8925">
                <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-8926">
                  Mode Explanations:
                </h5>
                <ul className="text-xs text-gray-600 space-y-2" data-id="element-8927">
                  <li data-id="element-8928">
                    <span className="font-semibold" data-id="element-8929">Override:</span> Default
                    approvers at this level will replace cost centre approvers
                  </li>
                  <li data-id="element-8930">
                    <span className="font-semibold" data-id="element-8931">Satisfy:</span> Default
                    approvers count as part of category sequence if they belong
                    to the category
                  </li>
                  <li data-id="element-8932">
                    <span className="font-semibold" data-id="element-8933">Cost Centre Only:</span>{' '}
                    Ignore default approvers at this level, use only cost centre
                    approvers
                  </li>
                </ul>
              </div>
            </div>}
        </div>
        {/* Delegation & Escalation Section */}
        <div className="border-b border-gray-200" data-id="element-8934">
          <button type="button" onClick={() => toggleSection('delegation')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-8935">
            <div className="flex items-center" data-id="element-8936">
              <ArrowUpRightIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-8937" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-8938">
                Delegation & Escalation
              </h3>
            </div>
            {expandedSections.delegation ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-8939" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-8940" />}
          </button>
          {expandedSections.delegation && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-8941">
              <p className="text-sm text-gray-500 mb-4" data-id="element-8942">
                Configure delegation and escalation settings
              </p>
              <div className="space-y-4" data-id="element-8943">
                <div className="flex items-center" data-id="element-8944">
                  <input id="inherit-limit" type="checkbox" checked={ruleData.delegation.inheritUpperLimit} onChange={e => handleDelegationChange('inheritUpperLimit', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-8945" />
                  <label htmlFor="inherit-limit" className="ml-3 block text-sm text-gray-700" data-id="element-8946">
                    Delegate inherits original approver's upper limit
                  </label>
                  <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-2" title="If enabled, a delegate will use the original approver's FDOA limit instead of their own" data-id="element-8947" />
                </div>
                <div className="flex items-center" data-id="element-8948">
                  <input id="original-chain" type="checkbox" checked={ruleData.delegation.useOriginalChain} onChange={e => handleDelegationChange('useOriginalChain', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-8949" />
                  <label htmlFor="original-chain" className="ml-3 block text-sm text-gray-700" data-id="element-8950">
                    Escalate along original approver's manager chain
                  </label>
                  <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-2" title="If enabled, escalation will follow the original approver's management chain rather than the delegate's" data-id="element-8951" />
                </div>
                <div data-id="element-8952">
                  <label htmlFor="max-hops" className="block text-sm font-medium text-gray-700" data-id="element-8953">
                    Maximum Escalation Hops
                  </label>
                  <div className="mt-1 w-24" data-id="element-8954">
                    <input type="number" id="max-hops" min="1" max="10" value={ruleData.delegation.maxHops} onChange={e => handleDelegationChange('maxHops', parseInt(e.target.value))} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8955" />
                  </div>
                  <p className="mt-1 text-xs text-gray-500" data-id="element-8956">
                    Maximum number of manager levels to escalate through
                  </p>
                </div>
              </div>
            </div>}
        </div>
        {/* Category Approvers Section */}
        <div className="border-b border-gray-200" data-id="element-8957">
          <button type="button" onClick={() => toggleSection('categories')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-8958">
            <div className="flex items-center" data-id="element-8959">
              <LayersIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-8960" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-8961">
                Category Approvers
              </h3>
            </div>
            {expandedSections.categories ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-8962" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-8963" />}
          </button>
          {expandedSections.categories && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-8964">
              <p className="text-sm text-gray-500 mb-4" data-id="element-8965">
                Configure approvers for each GL category (cost centre)
              </p>
              <div className="space-y-4" data-id="element-8966">
                {categoryApprovers.map((approver, index) => <div key={approver.id} className="border border-gray-200 rounded-md p-4" data-id="element-8967">
                    <div className="flex justify-between items-start" data-id="element-8968">
                      <div className="flex items-center" data-id="element-8969">
                        <LayersIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-8970" />
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-8971">
                          Category Approver (Sequence {approver.sequence})
                        </h4>
                      </div>
                      <button type="button" onClick={() => removeCategoryApprover(index)} className="text-red-600 hover:text-red-900" data-id="element-8972">
                        <TrashIcon className="h-4 w-4" data-id="element-8973" />
                      </button>
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-8974">
                      <div className="sm:col-span-3" data-id="element-8975">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-8976">
                          GL Category
                        </label>
                        <select value={approver.categoryId} onChange={e => updateCategoryApprover(index, 'categoryId', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-8977">
                          <option value="" data-id="element-8978">Select GL Category</option>
                          {glCategories.map(category => <option key={category.id} value={category.id} data-id="element-8979">
                              {category.code} - {category.description}
                            </option>)}
                        </select>
                      </div>
                      <div className="sm:col-span-1" data-id="element-8980">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-8981">
                          Sequence
                        </label>
                        <input type="number" min="1" value={approver.sequence} onChange={e => updateCategoryApprover(index, 'sequence', parseInt(e.target.value))} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-8982" />
                      </div>
                      <div className="sm:col-span-2" data-id="element-8983">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-8984">
                          User or Role
                        </label>
                        <select value={approver.userOrRoleId} onChange={e => updateCategoryApprover(index, 'userOrRoleId', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-8985">
                          <option value="" data-id="element-8986">Select User or Role</option>
                          <optgroup label="Users" data-id="element-8987">
                            {users.map(user => <option key={user.id} value={user.id} data-id="element-8988">
                                {user.name}
                              </option>)}
                          </optgroup>
                          <optgroup label="Roles" data-id="element-8989">
                            {roles.map(role => <option key={role.id} value={role.id} data-id="element-8990">
                                {role.name}
                              </option>)}
                          </optgroup>
                        </select>
                      </div>
                      <div className="sm:col-span-2" data-id="element-8991">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-8992">
                          Approval Limit
                        </label>
                        <div className="mt-1 flex rounded-md shadow-sm" data-id="element-8993">
                          <input type="number" value={approver.limit} onChange={e => updateCategoryApprover(index, 'limit', parseFloat(e.target.value))} className="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300" data-id="element-8994" />
                          <select value={approver.currency} onChange={e => updateCategoryApprover(index, 'currency', e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-8995">
                            {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-8996">
                                {currency.code}
                              </option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>)}
                <button type="button" onClick={addCategoryApprover} className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8997">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-8998" />
                  Add Category Approver
                </button>
              </div>
            </div>}
        </div>
        {/* Four-Eyes Section */}
        <div className="border-b border-gray-200" data-id="element-8999">
          <button type="button" onClick={() => toggleSection('fourEyes')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-9000">
            <div className="flex items-center" data-id="element-9001">
              <EyeIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-9002" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-9003">
                Four-Eyes Principle
              </h3>
            </div>
            {expandedSections.fourEyes ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-9004" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-9005" />}
          </button>
          {expandedSections.fourEyes && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-9006">
              <p className="text-sm text-gray-500 mb-4" data-id="element-9007">
                Configure Four-Eyes principle requiring two distinct approvers
              </p>
              <div className="flex items-center mb-4" data-id="element-9008">
                <input id="enable-four-eyes" type="checkbox" checked={ruleData.fourEyes.enabled} onChange={e => handleFourEyesChange('enabled', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-9009" />
                <label htmlFor="enable-four-eyes" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-9010">
                  Enable Four-Eyes Principle
                </label>
              </div>
              {ruleData.fourEyes.enabled && <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-3" data-id="element-9011">
                  <div data-id="element-9012">
                    <label htmlFor="four-eyes-threshold" className="block text-sm font-medium text-gray-700" data-id="element-9013">
                      Threshold
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm" data-id="element-9014">
                      <input type="number" id="four-eyes-threshold" value={ruleData.fourEyes.threshold} onChange={e => handleFourEyesChange('threshold', parseFloat(e.target.value))} className="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300" data-id="element-9015" />
                      <select value={ruleData.fourEyes.currency} onChange={e => handleFourEyesChange('currency', e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-9016">
                        {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-9017">
                            {currency.code}
                          </option>)}
                      </select>
                    </div>
                    <p className="mt-1 text-xs text-gray-500" data-id="element-9018">
                      Four-Eyes principle applies to transactions above this
                      amount
                    </p>
                  </div>
                  <div data-id="element-9019">
                    <label htmlFor="four-eyes-apply" className="block text-sm font-medium text-gray-700" data-id="element-9020">
                      Apply At
                    </label>
                    <select id="four-eyes-apply" value={ruleData.fourEyes.applyAt} onChange={e => handleFourEyesChange('applyAt', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-9021">
                      <option value="Header" data-id="element-9022">Header</option>
                      <option value="Line" data-id="element-9023">Line</option>
                      <option value="Both" data-id="element-9024">Both</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500" data-id="element-9025">
                      Where to apply the Four-Eyes check
                    </p>
                  </div>
                </div>}
              <div className="mt-4 bg-yellow-50 p-4 rounded-md" data-id="element-9026">
                <div className="flex" data-id="element-9027">
                  <div className="flex-shrink-0" data-id="element-9028">
                    <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-9029" />
                  </div>
                  <div className="ml-3" data-id="element-9030">
                    <h3 className="text-sm font-medium text-yellow-800" data-id="element-9031">
                      Four-Eyes Principle Note
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700" data-id="element-9032">
                      <p data-id="element-9033">When enabled, Four-Eyes requires that:</p>
                      <ul className="list-disc pl-5 mt-1 space-y-1" data-id="element-9034">
                        <li data-id="element-9035">
                          Two distinct approvers must approve transactions above
                          the threshold
                        </li>
                        <li data-id="element-9036">
                          The requester/coder of a document cannot be one of its
                          approvers
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>
        {/* Emergency Approvals Section */}
        <div className="border-b border-gray-200" data-id="element-9037">
          <button type="button" onClick={() => toggleSection('emergency')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-9038">
            <div className="flex items-center" data-id="element-9039">
              <AlertTriangleIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-9040" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-9041">
                Emergency Approvals
              </h3>
            </div>
            {expandedSections.emergency ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-9042" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-9043" />}
          </button>
          {expandedSections.emergency && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-9044">
              <p className="text-sm text-gray-500 mb-4" data-id="element-9045">
                Configure emergency approvers who can short-circuit normal
                approval flows
              </p>
              <div className="flex items-center mb-4" data-id="element-9046">
                <input id="enable-emergency" type="checkbox" checked={ruleData.emergency.enabled} onChange={e => handleEmergencyChange('enabled', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-9047" />
                <label htmlFor="enable-emergency" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-9048">
                  Enable Emergency Approvals
                </label>
              </div>
              {ruleData.emergency.enabled && <div data-id="element-9049">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-9050">
                    Emergency Approvers
                  </label>
                  <div className="mb-4" data-id="element-9051">
                    <select id="emergency-approver-select" onChange={e => {
                if (e.target.value) {
                  addEmergencyApprover(e.target.value);
                  e.target.value = '';
                }
              }} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-9052">
                      <option value="" data-id="element-9053">Add Emergency Approver</option>
                      <optgroup label="Users" data-id="element-9054">
                        {users.filter(user => !ruleData.emergency.approvers.includes(user.id)).map(user => <option key={user.id} value={user.id} data-id="element-9055">
                              {user.name}
                            </option>)}
                      </optgroup>
                      <optgroup label="Roles" data-id="element-9056">
                        {roles.filter(role => !ruleData.emergency.approvers.includes(role.id)).map(role => <option key={role.id} value={role.id} data-id="element-9057">
                              {role.name}
                            </option>)}
                      </optgroup>
                    </select>
                  </div>
                  {ruleData.emergency.approvers.length > 0 ? <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-9058">
                      {ruleData.emergency.approvers.map(approverId => {
                const user = users.find(u => u.id === approverId);
                const role = roles.find(r => r.id === approverId);
                const name = user?.name || role?.name || 'Unknown';
                const isUser = !!user;
                return <li key={approverId} className="px-4 py-3 flex items-center justify-between" data-id="element-9059">
                            <div className="flex items-center" data-id="element-9060">
                              {isUser ? <UserIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-9061" /> : <UsersIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-9062" />}
                              <span className="text-sm text-gray-900" data-id="element-9063">
                                {name}
                              </span>
                            </div>
                            <button type="button" onClick={() => removeEmergencyApprover(approverId)} className="text-red-600 hover:text-red-900" data-id="element-9064">
                              <TrashIcon className="h-4 w-4" data-id="element-9065" />
                            </button>
                          </li>;
              })}
                    </ul> : <div className="text-sm text-gray-500 italic" data-id="element-9066">
                      No emergency approvers selected
                    </div>}
                  <div className="mt-4 bg-yellow-50 p-4 rounded-md" data-id="element-9067">
                    <div className="flex" data-id="element-9068">
                      <div className="flex-shrink-0" data-id="element-9069">
                        <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-9070" />
                      </div>
                      <div className="ml-3" data-id="element-9071">
                        <h3 className="text-sm font-medium text-yellow-800" data-id="element-9072">
                          Emergency Approval Note
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700" data-id="element-9073">
                          <p data-id="element-9074">
                            Emergency approvers can short-circuit normal
                            approval flows in urgent situations. The Four-Eyes
                            principle will still be enforced if enabled.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>}
        </div>
        {/* Line-Level Evaluation Section */}
        <div className="border-b border-gray-200" data-id="element-9075">
          <button type="button" onClick={() => toggleSection('lineLevel')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-9076">
            <div className="flex items-center" data-id="element-9077">
              <LayersIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-9078" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-9079">
                Line-Level Evaluation
              </h3>
            </div>
            {expandedSections.lineLevel ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-9080" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-9081" />}
          </button>
          {expandedSections.lineLevel && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-9082">
              <p className="text-sm text-gray-500 mb-4" data-id="element-9083">
                Configure line-level and multi-cost-centre evaluation
              </p>
              <div className="space-y-4" data-id="element-9084">
                <div className="flex items-center" data-id="element-9085">
                  <input id="enable-line-level" type="checkbox" checked={ruleData.lineLevel.enabled} onChange={e => handleLineLevelChange('enabled', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-9086" />
                  <label htmlFor="enable-line-level" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-9087">
                    Enable Line-Level Evaluation
                  </label>
                </div>
                {ruleData.lineLevel.enabled && <>
                    <div data-id="element-9088">
                      <label htmlFor="evaluation-mode" className="block text-sm font-medium text-gray-700" data-id="element-9089">
                        Evaluation Mode
                      </label>
                      <select id="evaluation-mode" value={ruleData.lineLevel.evaluationMode} onChange={e => handleLineLevelChange('evaluationMode', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-9090">
                        <option value="Sequential" data-id="element-9091">Sequential</option>
                        <option value="Parallel" data-id="element-9092">Parallel</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500" data-id="element-9093">
                        Sequential: Lines are approved in sequence. Parallel:
                        All lines can be approved simultaneously.
                      </p>
                    </div>
                    <div className="flex items-center" data-id="element-9094">
                      <input id="categories-only" type="checkbox" checked={ruleData.lineLevel.categoriesOnly} onChange={e => handleLineLevelChange('categoriesOnly', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-9095" />
                      <label htmlFor="categories-only" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-9096">
                        Approvers only for categories they're authorised for
                      </label>
                      <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-2" title="When enabled, approvers will only see and approve lines for GL categories they are authorized for" data-id="element-9097" />
                    </div>
                  </>}
                <div className="mt-4 bg-gray-50 p-4 rounded-md" data-id="element-9098">
                  <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-9099">
                    Line-Level Evaluation:
                  </h5>
                  <ul className="text-xs text-gray-600 space-y-2" data-id="element-9100">
                    <li data-id="element-9101">
                      Evaluates each line item separately based on its GL
                      code/cost centre
                    </li>
                    <li data-id="element-9102">
                      Allows for different approvers for different types of
                      expenses
                    </li>
                    <li data-id="element-9103">Can handle documents with multiple cost centres</li>
                  </ul>
                </div>
              </div>
            </div>}
        </div>
        {/* Accounting Periods Section */}
        <div className="border-b border-gray-200" data-id="element-9104">
          <button type="button" onClick={() => toggleSection('periods')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-9105">
            <div className="flex items-center" data-id="element-9106">
              <CalendarIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-9107" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-9108">
                Accounting Periods
              </h3>
            </div>
            {expandedSections.periods ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-9109" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-9110" />}
          </button>
          {expandedSections.periods && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-9111">
              <p className="text-sm text-gray-500 mb-4" data-id="element-9112">
                Configure accounting periods and closed period checks
              </p>
              <div className="flex items-center mb-4" data-id="element-9113">
                <input id="check-closed-periods" type="checkbox" checked={ruleData.preApproval.checkClosedPeriods} onChange={e => setRuleData({
              ...ruleData,
              preApproval: {
                ...ruleData.preApproval,
                checkClosedPeriods: e.target.checked
              }
            })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-9114" />
                <label htmlFor="check-closed-periods" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-9115">
                  Check for closed accounting periods
                </label>
              </div>
              {ruleData.preApproval.checkClosedPeriods && <div className="space-y-4" data-id="element-9116">
                  <div className="overflow-x-auto" data-id="element-9117">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-9118">
                      <thead className="bg-gray-50" data-id="element-9119">
                        <tr data-id="element-9120">
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9121">
                            Entity
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9122">
                            Period Code
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9123">
                            Start Date
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9124">
                            End Date
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9125">
                            Status
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9126">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-9127">
                        {accountingPeriods.map((period, index) => {
                    const entity = entities.find(e => e.id === period.entityId);
                    return <tr key={period.id} data-id="element-9128">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-9129">
                                {entity?.name || 'Unknown Entity'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-9130">
                                {period.periodCode}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-9131">
                                {new Date(period.startUtc).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-9132">
                                {new Date(period.endUtc).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-9133">
                                {period.isClosed ? <StatusBadge status="error" text="Closed" data-id="element-9134" /> : <StatusBadge status="success" text="Open" data-id="element-9135" />}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-9136">
                                <div className="flex justify-end space-x-2" data-id="element-9137">
                                  <button type="button" onClick={() => updateAccountingPeriod(index, 'isClosed', !period.isClosed)} className={period.isClosed ? 'text-green-600 hover:text-green-900' : 'text-amber-600 hover:text-amber-900'} title={period.isClosed ? 'Open Period' : 'Close Period'} data-id="element-9138">
                                    {period.isClosed ? <CheckIcon className="h-4 w-4" data-id="element-9139" /> : <LockIcon className="h-4 w-4" data-id="element-9140" />}
                                  </button>
                                  <button type="button" onClick={() => removeAccountingPeriod(index)} className="text-red-600 hover:text-red-900" title="Remove Period" data-id="element-9141">
                                    <TrashIcon className="h-4 w-4" data-id="element-9142" />
                                  </button>
                                </div>
                              </td>
                            </tr>;
                  })}
                      </tbody>
                    </table>
                  </div>
                  <button type="button" onClick={addAccountingPeriod} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9143">
                    <PlusIcon className="h-4 w-4 mr-2" data-id="element-9144" />
                    Add Accounting Period
                  </button>
                </div>}
            </div>}
        </div>
        {/* Multi-Currency Section */}
        <div className="border-b border-gray-200" data-id="element-9145">
          <button type="button" onClick={() => toggleSection('multiCurrency')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-9146">
            <div className="flex items-center" data-id="element-9147">
              <DollarSignIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-9148" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-9149">
                Multi-Currency
              </h3>
            </div>
            {expandedSections.multiCurrency ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-9150" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-9151" />}
          </button>
          {expandedSections.multiCurrency && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-9152">
              <p className="text-sm text-gray-500 mb-4" data-id="element-9153">
                Configure multi-currency support
              </p>
              <div className="space-y-4" data-id="element-9154">
                <div className="flex items-center" data-id="element-9155">
                  <input id="enable-multi-currency" type="checkbox" checked={ruleData.multiCurrency.enabled} onChange={e => handleMultiCurrencyChange('enabled', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-9156" />
                  <label htmlFor="enable-multi-currency" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-9157">
                    Enable Multi-Currency Support
                  </label>
                </div>
                {ruleData.multiCurrency.enabled && <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-3" data-id="element-9158">
                    <div data-id="element-9159">
                      <label htmlFor="base-currency" className="block text-sm font-medium text-gray-700" data-id="element-9160">
                        Base Currency
                      </label>
                      <select id="base-currency" value={ruleData.multiCurrency.baseCurrency} onChange={e => handleMultiCurrencyChange('baseCurrency', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-9161">
                        {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-9162">
                            {currency.code} - {currency.name}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-9163">
                      <label htmlFor="evaluate-at" className="block text-sm font-medium text-gray-700" data-id="element-9164">
                        Evaluate Thresholds At
                      </label>
                      <select id="evaluate-at" value={ruleData.multiCurrency.evaluateAt} onChange={e => handleMultiCurrencyChange('evaluateAt', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-9165">
                        <option value="Header" data-id="element-9166">Header (Document Total)</option>
                        <option value="Line" data-id="element-9167">Line (Line Subtotals)</option>
                      </select>
                    </div>
                  </div>}
                <div className="mt-4 bg-gray-50 p-4 rounded-md" data-id="element-9168">
                  <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-9169">
                    Multi-Currency Support:
                  </h5>
                  <ul className="text-xs text-gray-600 space-y-2" data-id="element-9170">
                    <li data-id="element-9171">
                      Converts transaction amounts to the base currency for
                      threshold evaluation
                    </li>
                    <li data-id="element-9172">Uses current exchange rates from the system</li>
                    <li data-id="element-9173">
                      Allows approvers to have limits in different currencies
                    </li>
                  </ul>
                </div>
              </div>
            </div>}
        </div>
        {/* Audit Section */}
        <div className="border-b border-gray-200" data-id="element-9174">
          <button type="button" onClick={() => toggleSection('audit')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-9175">
            <div className="flex items-center" data-id="element-9176">
              <ClockIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-9177" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-9178">Audit</h3>
            </div>
            {expandedSections.audit ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-9179" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-9180" />}
          </button>
          {expandedSections.audit && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-9181">
              <p className="text-sm text-gray-500 mb-4" data-id="element-9182">
                Configure audit settings
              </p>
              <div className="space-y-4" data-id="element-9183">
                <div className="flex items-center" data-id="element-9184">
                  <input id="enable-audit" type="checkbox" checked={ruleData.audit.enabled} onChange={e => setRuleData({
                ...ruleData,
                audit: {
                  ...ruleData.audit,
                  enabled: e.target.checked
                }
              })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-9185" />
                  <label htmlFor="enable-audit" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-9186">
                    Enable Audit Logging
                  </label>
                </div>
                {ruleData.audit.enabled && <div className="flex items-center" data-id="element-9187">
                    <input id="store-decisions" type="checkbox" checked={ruleData.audit.storeDecisions} onChange={e => setRuleData({
                ...ruleData,
                audit: {
                  ...ruleData.audit,
                  storeDecisions: e.target.checked
                }
              })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-9188" />
                    <label htmlFor="store-decisions" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-9189">
                      Store Rule Decisions
                    </label>
                    <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-2" title="When enabled, all rule decisions (Approved, Escalated, Failed, etc.) will be stored in the audit log" data-id="element-9190" />
                  </div>}
                <div className="mt-4 bg-gray-50 p-4 rounded-md" data-id="element-9191">
                  <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-9192">
                    Audit Features:
                  </h5>
                  <ul className="text-xs text-gray-600 space-y-2" data-id="element-9193">
                    <li data-id="element-9194">
                      Append-only audit log ensures all rule evaluations are
                      recorded
                    </li>
                    <li data-id="element-9195">
                      Tamper-evident hashing ensures audit trail integrity
                    </li>
                    <li data-id="element-9196">
                      Compliant with ISO27001 and other regulatory requirements
                    </li>
                  </ul>
                </div>
              </div>
            </div>}
        </div>
      </div>
      <div className="flex justify-end space-x-3" data-id="element-9197">
        <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9198">
          Cancel
        </button>
        <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9199">
          <SaveIcon className="h-4 w-4 mr-2" data-id="element-9200" />
          Save Rule
        </button>
      </div>
    </form>;
}